import React, { useState } from "react";
import { useQuery } from "react-query";
import { Typography } from "antd";
import { SearchOrganizationInput } from "@secondcloset/web-components";
import { fetchOrganizationIndex } from "../../../../../api/accounts/organization";
import { useDebounce } from "../../../../../components/ratecard/hooks";
import "./ParentOrganizationSection.scss";

const { Text } = Typography;

interface ParentOrganizationSectionProps {
  selectedParentOrg: { label: string; value: string } | undefined;
  onParentOrganizationChange: (orgId: string | null, orgName: string) => void;
}

type OrgOption = { label: string; value: string };

const ParentOrganizationSection: React.FC<ParentOrganizationSectionProps> = ({
  selectedParentOrg,
  onParentOrganizationChange,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, 500);

  // Initialize search value from selected parent org
  React.useEffect(() => {
    if (selectedParentOrg) {
      setSearchValue(selectedParentOrg.label);
    } else {
      setSearchValue("");
    }
  }, [selectedParentOrg]);

  const organizationLookup = useQuery(
    ["organizationLookup", debouncedSearch],
    () => fetchOrganizationIndex({ q: String(debouncedSearch).trim() }),
    {
      enabled: String(debouncedSearch).trim().length >= 3,
      keepPreviousData: true,
    }
  );

  const handleOrgSelect = (org: OrgOption | undefined) => {
    if (!org) {
      handleClear();
      return;
    }
    setSearchValue(org.label);
    onParentOrganizationChange(org.value, org.label);
  };

  const handleSearchChange = (search: string) => {
    setSearchValue(search);
  };

  const handleClear = () => {
    setSearchValue("");
    onParentOrganizationChange(null, "");
  };

  return (
    <div className="parent-organization-section">
      <h3 className="section-header">Parent Organization</h3>
      <Text type="secondary">Parent organization has access to this organization and can manage it.</Text>

      <div className="search-container">
        <SearchOrganizationInput<OrgOption>
          mode={undefined}
          showSearch
          placeholder="No parent organization set. Start typing to search for a parent organization"
          searchValue={searchValue}
          value={selectedParentOrg}
          loading={organizationLookup.isLoading}
          organizations={organizationLookup.data ?? []}
          onSearch={handleSearchChange}
          onClear={handleClear}
          onChange={handleOrgSelect}
        />
      </div>
    </div>
  );
};

export default ParentOrganizationSection;
